import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, formatDateRange } from '../../lib/api';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Users, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  ArrowRight,
  FileText,
  DollarSign,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await adminApi.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  const pendingTotal = (stats?.pending_entries || 0) + (stats?.pending_commissions || 0);

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
              Dashboard
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {stats?.current_period && (
                <>Current period: <span className="font-medium text-slate-700">{formatDateRange(stats.current_period.start_date, stats.current_period.end_date)}</span></>
              )}
            </p>
          </div>
          {pendingTotal > 0 && (
            <Link to="/admin/timesheets">
              <Button className="bg-brick-red hover:bg-[#c4151d] font-medium" data-testid="review-pending-btn">
                <AlertCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                Review {pendingTotal} Pending
              </Button>
            </Link>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Team</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="total-contractors">
                    {stats?.total_contractors || 0}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-slate-600" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pending</p>
                  <p className="text-3xl font-bold text-amber-600 mt-1" data-testid="pending-entries">
                    {pendingTotal}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Hours</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="period-hours">
                    {stats?.current_period?.total_hours?.toFixed(1) || '0'}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-brick-cyan/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-brick-cyan" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Approved</p>
                  <p className="text-3xl font-bold text-green-600 mt-1" data-testid="approved-hours">
                    {stats?.current_period?.approved_hours?.toFixed(1) || '0'}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/admin/timesheets" className="block group">
            <Card className="bg-white border border-slate-200 shadow-sm h-full hover:border-brick-cyan hover:shadow-md transition-all">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <Clock className="w-8 h-8 text-brick-cyan" aria-hidden="true" />
                  {stats?.pending_entries > 0 && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                      {stats.pending_entries} new
                    </Badge>
                  )}
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mt-4 group-hover:text-brick-cyan transition-colors">
                  Timesheets
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Review and approve hours
                </p>
                <div className="flex items-center text-sm font-medium text-brick-cyan mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  View all <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/commissions" className="block group">
            <Card className="bg-white border border-slate-200 shadow-sm h-full hover:border-brick-cyan hover:shadow-md transition-all">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <DollarSign className="w-8 h-8 text-brick-cyan" aria-hidden="true" />
                  {stats?.pending_commissions > 0 && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                      {stats.pending_commissions} new
                    </Badge>
                  )}
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mt-4 group-hover:text-brick-cyan transition-colors">
                  Commissions
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Review sales commissions
                </p>
                <div className="flex items-center text-sm font-medium text-brick-cyan mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  View all <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/contractors" className="block group">
            <Card className="bg-white border border-slate-200 shadow-sm h-full hover:border-brick-cyan hover:shadow-md transition-all">
              <CardContent className="p-5">
                <Users className="w-8 h-8 text-brick-cyan" aria-hidden="true" />
                <h3 className="font-heading text-lg font-semibold text-slate-900 mt-4 group-hover:text-brick-cyan transition-colors">
                  Team
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Manage contractors & rates
                </p>
                <div className="flex items-center text-sm font-medium text-brick-cyan mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  View all <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/reports" className="block group">
            <Card className="bg-white border border-slate-200 shadow-sm h-full hover:border-brick-cyan hover:shadow-md transition-all">
              <CardContent className="p-5">
                <FileText className="w-8 h-8 text-brick-cyan" aria-hidden="true" />
                <h3 className="font-heading text-lg font-semibold text-slate-900 mt-4 group-hover:text-brick-cyan transition-colors">
                  Reports
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Export payroll data
                </p>
                <div className="flex items-center text-sm font-medium text-brick-cyan mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  View all <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Period Summary */}
        {stats?.current_period && (
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900">Period Summary</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {formatDateRange(stats.current_period.start_date, stats.current_period.end_date)}
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Entries</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.current_period.entries_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Hours</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.current_period.total_hours?.toFixed(1) || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pending Hours</p>
                    <p className="text-2xl font-bold text-amber-600">{stats.current_period.pending_hours?.toFixed(1) || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Commissions</p>
                    <p className="text-2xl font-bold text-green-600">
                      ${stats.current_period.total_commission_amount?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default AdminDashboard;
